// ============================================================
//  Threat Intelligence — phishing & security news aggregator
// ============================================================
//
// Pulls the latest phishing / social-engineering stories from the top
// cybersecurity outlets (BleepingComputer, The Hacker News, Dark Reading,
// Security Affairs, Krebs on Security, The Register, …) via the server-side
// aggregator at /api/threat-intel and renders them as a filterable card feed.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaRss,
  FaSyncAlt,
  FaSpinner,
  FaExternalLinkAlt,
  FaSearch,
  FaExclamationTriangle,
  FaBrain,
} from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import ThreatInsights from "./ThreatInsights";
import "../PhishingAnalysis/PhishingAnalysis.css";
import "./ThreatIntelligence.css";

const fmtRelative = (iso) => {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
};

// Stable accent colour per source so the badges are easy to scan.
const SOURCE_HUES = {
  bleepingcomputer: 205,
  thehackernews: 12,
  darkreading: 280,
  securityaffairs: 145,
  krebsonsecurity: 35,
  theregister: 0,
  infosecurity: 250,
  thecyberexpress: 320,
};
const sourceStyle = (id) => {
  const hue = SOURCE_HUES[id] ?? Math.abs(hashCode(id)) % 360;
  return { "--src-hue": hue };
};
function hashCode(s = "") {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h << 5) - h + s.charCodeAt(i);
  return h;
}

export default function ThreatIntelligence() {
  const { token } = useAuth();

  const [items, setItems] = useState([]);
  const [sources, setSources] = useState([]);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  // Top-level tab: the aggregate insights view (default) vs. the news feed.
  const [tab, setTab] = useState("insights"); // "insights" | "feed"

  // Filters
  const [scope, setScope] = useState("phishing"); // "phishing" | "all"
  const [activeSource, setActiveSource] = useState(""); // "" = all sources
  const [query, setQuery] = useState("");

  const authHeaders = useCallback(
    (extra = {}) => ({ Authorization: `Bearer ${token}`, ...extra }),
    [token]
  );

  const load = useCallback(
    async ({ force = false } = {}) => {
      setError("");
      if (force) setRefreshing(true);
      try {
        const params = new URLSearchParams({ scope });
        if (force) params.set("refresh", "1");
        const res = await fetch(`/api/threat-intel?${params.toString()}`, {
          headers: authHeaders(),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
        setItems(json.items || []);
        setSources(json.sources || []);
        setFetchedAt(json.fetchedAt || null);
      } catch (e) {
        setError(e.message || "Failed to load threat intelligence");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [authHeaders, scope]
  );

  // Reload whenever the scope changes (server re-filters phishing vs all).
  useEffect(() => {
    load();
  }, [load]);

  // Source + text filtering happens client-side over the loaded set.
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((it) => {
      if (activeSource && it.source !== activeSource) return false;
      if (q && !`${it.title} ${it.summary}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, activeSource, query]);

  // Per-source counts for the filter chips (within the current scope).
  const counts = useMemo(() => {
    const map = {};
    for (const it of items) map[it.source] = (map[it.source] || 0) + 1;
    return map;
  }, [items]);

  const failedSources = sources.filter((s) => !s.ok);

  return (
    <div className="phish-page">
      <section className="phish-shell">
        {/* Title + tab switch */}
        <div className="card upload-card ti-head-card">
          <div className="upload-header">
            <div>
              <h2 className="section-title">
                <FaRss style={{ marginRight: 8 }} />
                Threat Intelligence
              </h2>
              <p className="section-desc">
                Phishing and security activity curated from the top cybersecurity sources —
                browse the live feed or jump to AI-correlated insights.
              </p>
            </div>
          </div>

          <div className="ti-tabs" role="tablist" aria-label="Threat Intelligence view">
            <button
              type="button"
              role="tab"
              aria-selected={tab === "feed"}
              className={`ti-tab ${tab === "feed" ? "active" : ""}`}
              onClick={() => setTab("feed")}
            >
              <FaRss aria-hidden /> Feed
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "insights"}
              className={`ti-tab ${tab === "insights" ? "active" : ""}`}
              onClick={() => setTab("insights")}
            >
              <FaBrain aria-hidden /> Insights
            </button>
          </div>
        </div>

        {tab === "insights" ? (
          <ThreatInsights />
        ) : (
          <>
            {/* Feed controls */}
            <div className="card upload-card">
              <div className="upload-header">
                <div className="ti-feed-meta">
                  {fetchedAt && (
                    <span className="ti-updated">Updated {fmtRelative(fetchedAt)}</span>
                  )}
                </div>

                <div className="ti-actions">
                  <div className="ti-scope-toggle" role="tablist" aria-label="Scope">
                    <button
                      type="button"
                      className={`ti-scope-btn ${scope === "phishing" ? "active" : ""}`}
                      onClick={() => setScope("phishing")}
                    >
                      Phishing
                    </button>
                    <button
                      type="button"
                      className={`ti-scope-btn ${scope === "all" ? "active" : ""}`}
                      onClick={() => setScope("all")}
                    >
                      All Security
                    </button>
                  </div>
                  <button
                    type="button"
                    className="ti-refresh-btn"
                    onClick={() => load({ force: true })}
                    disabled={refreshing}
                    title="Re-pull the latest stories from all sources"
                  >
                    {refreshing ? <FaSpinner className="spin" /> : <FaSyncAlt />}
                    {refreshing ? " Refreshing…" : " Refresh"}
                  </button>
                </div>
              </div>

              {/* Search + source chips */}
              <div className="ti-filters">
            <div className="ti-search">
              <FaSearch className="ti-search-icon" aria-hidden />
              <input
                type="text"
                placeholder="Filter by keyword…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>

            <div className="ti-source-chips">
              <button
                type="button"
                className={`ti-chip ${activeSource === "" ? "active" : ""}`}
                onClick={() => setActiveSource("")}
              >
                All sources <span className="ti-chip-count">{items.length}</span>
              </button>
              {sources
                .filter((s) => counts[s.id])
                .map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className={`ti-chip ${activeSource === s.id ? "active" : ""}`}
                    style={sourceStyle(s.id)}
                    onClick={() => setActiveSource(activeSource === s.id ? "" : s.id)}
                  >
                    {s.name} <span className="ti-chip-count">{counts[s.id]}</span>
                  </button>
                ))}
            </div>
          </div>

          {failedSources.length > 0 && (
            <div className="ti-warn">
              <FaExclamationTriangle />{" "}
              Couldn’t reach {failedSources.map((s) => s.name).join(", ")}. Showing the rest.
            </div>
          )}
          {error && <div className="error-banner">{error}</div>}
        </div>

        {/* Feed */}
        <div className="card ti-feed-card">
          {loading ? (
            <p className="placeholder">
              <FaSpinner className="spin" /> Loading threat intelligence…
            </p>
          ) : visible.length === 0 ? (
            <p className="placeholder">
              No stories match the current filters{scope === "phishing" ? " (try “All Security”)" : ""}.
            </p>
          ) : (
            <div className="ti-grid">
              {visible.map((it) => (
                <a
                  key={it.id}
                  href={it.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ti-item"
                  style={sourceStyle(it.source)}
                >
                  <div className="ti-item-top">
                    <span className="ti-source-badge">{it.sourceName}</span>
                    <span className="ti-time">{fmtRelative(it.publishedAt)}</span>
                  </div>
                  <h3 className="ti-item-title">
                    {it.title}
                    <FaExternalLinkAlt className="ti-ext" aria-hidden />
                  </h3>
                  {it.summary && <p className="ti-item-summary">{it.summary}</p>}
                  {it.tags && it.tags.length > 0 && (
                    <div className="ti-tags">
                      {it.tags.map((t) => (
                        <span key={t} className="ti-tag">#{t}</span>
                      ))}
                    </div>
                  )}
                </a>
              ))}
            </div>
          )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
